// Telegram Web App API integration
export function isTelegram() {
  if (typeof window === 'undefined') return false;
  const webApp = window.Telegram?.WebApp;
  if (!webApp) return false;
  const hasInitData = typeof webApp.initData === 'string' && webApp.initData.trim().length > 0;
  const hasUserId = Number.isFinite(Number(webApp.initDataUnsafe?.user?.id));
  return hasInitData || hasUserId;
}

/**
 * Страница открыта внутри клиента Telegram — даже без initData (кнопка клавиатуры, сбой клиента).
 * Только для вёрстки: кнопки «Назад» / ˅ ⋯ всё равно рисуются поверх webview.
 */
export function isTelegramShell() {
  if (isTelegram()) return true;
  if (typeof window === 'undefined') return false;
  const platform = String(window.Telegram?.WebApp?.platform || '').toLowerCase();
  return platform !== '' && platform !== 'unknown';
}

/** Платформы Telegram с «десктопной» полосой заголовка (без mobile notch). */
const TG_DESKTOP_PLATFORMS = new Set(['tdesktop', 'macos', 'weba', 'webk', 'unigram', 'web']);

export function isTelegramDesktopLikePlatform(platform) {
  const p = String(platform || '').toLowerCase();
  return TG_DESKTOP_PLATFORMS.has(p);
}

/**
 * Telegram Desktop / Web / macOS или широкий экран с мышью (планшет, окно TG на ПК).
 * Для таких клиентов шапка BF — сразу под кнопками «Закрыть» / ⋮, без mobile safe-area 88px.
 */
export function isTelegramWideLayout(tg = null) {
  if (typeof window === 'undefined') return false;
  const webApp = tg || window.Telegram?.WebApp;
  if (webApp && isTelegramDesktopLikePlatform(webApp.platform)) return true;
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(min-width: 768px) and (pointer: fine)').matches;
}

function clearBfTelegramDomFlags() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.removeAttribute('data-tg-webapp');
  root.removeAttribute('data-tg-desktop');
  root.removeAttribute('data-tg-fullscreen');
  root.removeAttribute('data-tg-expanded');
  root.removeAttribute('data-tg-platform');
  root.style.setProperty('--bf-tg-content-top', '0px');
  root.style.setProperty('--bf-tg-content-bottom', '0px');
  root.style.setProperty('--bf-tg-chrome-mid', '0px');
  root.style.removeProperty('--tg-viewport-height');
  root.style.removeProperty('--tg-viewport-stable-height');
  root.removeAttribute('data-tg-visual-inset');
  root.style.removeProperty('--tg-visual-offset-left');
  root.style.removeProperty('--tg-visual-width');
}

function applyTelegramViewportCss(tg) {
  if (typeof document === 'undefined' || !tg) return;
  const root = document.documentElement;
  const vh = Number(tg.viewportHeight) > 0 ? tg.viewportHeight : window.innerHeight;
  const vsh =
    Number(tg.viewportStableHeight) > 0 ? tg.viewportStableHeight : vh;
  root.style.setProperty('--tg-viewport-height', `${Math.round(vh)}px`);
  root.style.setProperty('--tg-viewport-stable-height', `${Math.round(vsh)}px`);
  if (tg.isExpanded === true) root.setAttribute('data-tg-expanded', 'true');
  else root.removeAttribute('data-tg-expanded');
}

/** Полный экран просим только при запуске; вышел сам — больше не возвращаем. */
const fullscreenState = { attempts: 0, wasFullscreen: false, userExited: false };
const MAX_FULLSCREEN_ATTEMPTS = 2;

function tryTelegramFullscreen(tg) {
  if (!tg || isTelegramWideLayout(tg)) return;
  if (tg.isFullscreen === true || fullscreenState.userExited) return;
  if (fullscreenState.attempts >= MAX_FULLSCREEN_ATTEMPTS) return;
  if (typeof tg.requestFullscreen !== 'function') return;
  if (typeof tg.isVersionAtLeast === 'function' && !tg.isVersionAtLeast('8.0')) return;
  fullscreenState.attempts += 1;
  try {
    tg.requestFullscreen();
  } catch {
    /* старые клиенты / политика платформы */
  }
}

function trackFullscreenExit(tg) {
  if (!tg) return;
  if (tg.isFullscreen === true) {
    fullscreenState.wasFullscreen = true;
  } else if (fullscreenState.wasFullscreen) {
    fullscreenState.userExited = true;
  }
}

const insetNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0);

/** Строка «Закрыть» / ⋮ в mobile fullscreen, если клиент не прислал contentSafeAreaInset. */
const TG_MOBILE_CHROME_FALLBACK = 46;

/** Смещение visualViewport (iOS sheet) — иначе контент уезжает влево и обрезается. */
function applyTelegramVisualViewportCss() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const root = document.documentElement;
  const vv = window.visualViewport;
  if (!vv || !isTelegramShell()) {
    root.removeAttribute('data-tg-visual-inset');
    root.style.removeProperty('--tg-visual-offset-left');
    root.style.removeProperty('--tg-visual-width');
    return;
  }
  const offsetLeft = Math.max(0, Math.round(vv.offsetLeft || 0));
  const offsetTop = Math.max(0, Math.round(vv.offsetTop || 0));
  const vWidth = Math.round(vv.width || window.innerWidth);
  const layoutW = window.innerWidth || vWidth;
  const needsInset =
    offsetLeft > 1 ||
    offsetTop > 1 ||
    (layoutW > 0 && vWidth > 0 && vWidth < layoutW - 2);
  if (!needsInset) {
    root.removeAttribute('data-tg-visual-inset');
    root.style.removeProperty('--tg-visual-offset-left');
    root.style.removeProperty('--tg-visual-width');
    return;
  }
  root.setAttribute('data-tg-visual-inset', 'true');
  root.style.setProperty('--tg-visual-offset-left', `${offsetLeft}px`);
  root.style.setProperty('--tg-visual-width', `${vWidth}px`);
}

class TelegramService {
  constructor() {
    const tg = this.getWebApp();
    const inTelegram = isTelegramShell();
    if (!inTelegram) {
      clearBfTelegramDomFlags();
    }
    if (tg && inTelegram) {
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-tg-webapp', 'true');
        this.syncTelegramLayoutAttributes(tg);
      }
      tg.ready();
      tg.expand();
      tg.disableVerticalSwipes();
      applyTelegramViewportCss(tg);
      applyTelegramVisualViewportCss();
      this.applyBfTopInsetFromTelegram();
      tryTelegramFullscreen(tg);
      queueMicrotask(() => {
        applyTelegramViewportCss(tg);
        applyTelegramVisualViewportCss();
        this.applyBfTopInsetFromTelegram();
      });
      window.setTimeout(() => {
        applyTelegramViewportCss(tg);
        applyTelegramVisualViewportCss();
        this.applyBfTopInsetFromTelegram();
      }, 320);
      window.setTimeout(() => {
        applyTelegramViewportCss(tg);
        applyTelegramVisualViewportCss();
        this.applyBfTopInsetFromTelegram();
        tryTelegramFullscreen(tg);
      }, 720);
      this.attachBfTopInsetListeners();

      // Скрыть кнопку настроек
      if (tg?.SettingsButton) {
        try {
          // Добавляем обработчик onClick для интеграции с нашим меню
          tg.SettingsButton.onClick(() => {
            console.log('🔔 SettingsButton clicked!');
            // Открываем наше меню через событие
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('telegram-settings-click'));
            }
          });
          
          // Сохраняем ссылку на SettingsButton для внешнего доступа
          this.settingsButton = tg.SettingsButton;
          
          // Скрываем кнопку
          tg.SettingsButton.hide();
          console.log('✅ SettingsButton hidden');
        } catch (e) {
          console.log('❌ SettingsButton error:', e.message);
        }
      }
    }
  }

  getWebApp() {
    if (typeof window === 'undefined') return null;
    return window.Telegram?.WebApp || null;
  }

  /**
   * Вертикальные inset'ы для BF: верх (кнопки «Закрыть» / ⋮) и низ (home / зона жестов).
   * В fullscreen Mini App `contentSafeAreaInset` дополняет системный env(safe-area-*).
   */
  syncTelegramLayoutAttributes(tg) {
    if (typeof document === 'undefined') return;
    const wide = isTelegramWideLayout(tg);
    const root = document.documentElement;
    if (wide) root.setAttribute('data-tg-desktop', 'true');
    else root.removeAttribute('data-tg-desktop');
    if (tg?.isFullscreen === true) root.setAttribute('data-tg-fullscreen', 'true');
    else root.removeAttribute('data-tg-fullscreen');
    applyTelegramViewportCss(tg);
    if (tg?.platform) {
      document.documentElement.setAttribute('data-tg-platform', String(tg.platform).toLowerCase());
    }
  }

  applyBfTopInsetFromTelegram() {
    if (typeof document === 'undefined') return;
    const tg = this.getWebApp();
    if (!tg || !isTelegramShell()) {
      clearBfTelegramDomFlags();
      return;
    }
    if (!document.documentElement.hasAttribute('data-tg-webapp')) {
      document.documentElement.setAttribute('data-tg-webapp', 'true');
    }

    this.syncTelegramLayoutAttributes(tg);

    // По Bot API 8: safeAreaInset — системные полосы устройства, contentSafeAreaInset — кнопки
    // Telegram внутри них. Отступы складываются. Вне fullscreen шапка Telegram снаружи webview.
    const c = tg.contentSafeAreaInset;
    const s = tg.safeAreaInset;
    const wideLayout = isTelegramWideLayout(tg);
    const fullscreen = tg.isFullscreen === true;

    const statusTop = insetNumber(s?.top);
    let chromeTop = insetNumber(c?.top);
    if (fullscreen && chromeTop === 0 && !wideLayout) chromeTop = TG_MOBILE_CHROME_FALLBACK;
    const top = statusTop + chromeTop;

    const tgChromeMid = Math.round(statusTop + chromeTop / 2);
    document.documentElement.style.setProperty('--bf-tg-chrome-mid', `${tgChromeMid}px`);
    document.documentElement.style.setProperty('--bf-tg-content-top', `${Math.round(top)}px`);

    const bottom = insetNumber(s?.bottom) + insetNumber(c?.bottom);
    document.documentElement.style.setProperty('--bf-tg-content-bottom', `${Math.round(bottom)}px`);
  }

  attachBfTopInsetListeners() {
    const tg = this.getWebApp();
    if (!tg || typeof window === 'undefined') return;
    const apply = () => {
      const webApp = this.getWebApp();
      if (webApp) {
        trackFullscreenExit(webApp);
        applyTelegramViewportCss(webApp);
      }
      applyTelegramVisualViewportCss();
      this.applyBfTopInsetFromTelegram();
    };
    const events = [
      'contentSafeAreaChanged',
      'safeAreaChanged',
      'viewportChanged',
      'fullscreenChanged',
    ];
    events.forEach((ev) => {
      try {
        tg.onEvent(ev, apply);
      } catch {
        /* старые клиенты без Bot API 8+ */
      }
    });
    window.addEventListener('resize', apply);
    window.visualViewport?.addEventListener('resize', apply);
    window.visualViewport?.addEventListener('scroll', apply);
  }

  get isTelegramWebApp() {
    return isTelegram();
  }

  get isTelegramWideLayout() {
    return isTelegramWideLayout(this.getWebApp());
  }

  // Get user data from Telegram
  getUser() {
    const tg = this.getWebApp();
    if (!tg) return null;
    
    const user = tg.initDataUnsafe?.user;
    return user ? {
      id: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      username: user.username,
      languageCode: user.language_code
    } : null;
  }

  // Get Telegram init data (contains user info and auth token)
  getInitData() {
    const tg = this.getWebApp();
    if (!tg) return null;
    return tg.initData || null;
  }

  // Get Telegram init data as object
  getInitDataUnsafe() {
    const tg = this.getWebApp();
    if (!tg) return null;
    return tg.initDataUnsafe || null;
  }

  // Get auth token from Telegram
  getAuthToken() {
    const tg = this.getWebApp();
    if (!tg) return null;
    
    // Get user data directly from Telegram Web App
    const user = this.getUser();
    console.log('🔍 Telegram user:', user);
    
    if (user) {
      // Return user data for authentication
      return user;
    }
    return null;
  }

  // Show main button
  showMainButton(text, onClick) {
    const tg = this.getWebApp();
    if (!tg) return;
    
    tg.MainButton.setText(text);
    tg.MainButton.onClick(onClick);
    tg.MainButton.show();
  }

  // Hide main button
  hideMainButton() {
    const tg = this.getWebApp();
    if (!tg) return;
    tg.MainButton.hide();
  }

  // Show back button
  showBackButton(onClick) {
    const tg = this.getWebApp();
    if (!tg) return;
    
    tg.BackButton.onClick(onClick);
    tg.BackButton.show();
  }

  // Hide back button
  hideBackButton() {
    const tg = this.getWebApp();
    if (!tg) return;
    tg.BackButton.hide();
  }

  // Close Web App
  close() {
    const tg = this.getWebApp();
    if (!tg) return;
    tg.close();
  }

  // Send data to bot
  sendData(data) {
    const tg = this.getWebApp();
    if (!tg) return;
    tg.sendData(JSON.stringify(data));
  }

  // Show alert
  showAlert(message) {
    const tg = this.getWebApp();
    if (!tg) return;
    tg.showAlert(message);
  }

  // Show confirm
  showConfirm(message, callback) {
    const tg = this.getWebApp();
    if (!tg) return;
    tg.showConfirm(message, callback);
  }

  // Haptic feedback
  hapticFeedback(type = 'medium') {
    const tg = this.getWebApp();
    if (!tg) return;
    tg.HapticFeedback.impactOccurred(type);
  }

  // Get SettingsButton reference
  getSettingsButton() {
    return this.settingsButton || null;
  }
}

export const telegramService = new TelegramService();
